import { InputBaseProps, Select, SelectProps, Typography } from '@mui/material';
import {
	ElementType,
	ForwardedRef,
	forwardRef,
	ReactElement,
	Ref,
	useCallback,
	useMemo,
	useRef,
} from 'react';
import { Option } from '../core-autocomplete';
import { CoreFormControl, CoreFormControlProps } from '../core-form-control';
import { CoreInput, CoreInputProps } from '../core-input';
import { CoreSelectMenuItem, CoreSelectMenuItemProps } from './core-select-menu-item.component';
import { selectPaperStyles } from './core-select.styles';
import { clsx } from '../../../utils';
import { Colors, inputPlaceholderColor } from '../../../constants';
import { ArrowDownIcon } from '../../../icons';

export type SelectOption = {
	value: Option['value'];
	label: Option['label'] | JSX.Element;
	icon?: JSX.Element;
	disabled?: boolean;
};

export interface CoreSelectProps<Value = unknown> extends
	CoreFormControlProps,
	Omit<SelectProps<Value>, 'input' | 'inputProps' | 'variant'>
{
	menuMinWidth?: number;
	emptyLabel?: string;
	fullWidth?: boolean;
	IconComponent?: ElementType;
	fitMenuWidth?: boolean;
	disableIconRotate?: boolean;
	inputProps?: InputBaseProps & CoreInputProps;
	options?: SelectOption[];
	MenuItemProps?: CoreSelectMenuItemProps;
	renderOption?(option: SelectOption): JSX.Element;
}

// eslint-disable-next-line complexity
const CoreSelectBase = <Value = unknown>(
	props: CoreSelectProps<Value>,
	ref: ForwardedRef<HTMLElement>
): ReactElement => {
	const {
		id,
		children,
		options,
		focused,
		disabled,
		error,
		helperText,
		fullWidth,
		controlSx,
		label,
		labelSx,
		labelHint,
		requiredMark,
		labelHintIconSx,
		tooltipSx,
		placementOffset,
		placementLabelHint,
		menuMinWidth,
		displayEmpty,
		disableIconRotate,
		fitMenuWidth,
		value,
		placeholder,
		sx,
		inputProps,
		MenuItemProps,
		MenuProps,
		SelectDisplayProps,
		IconComponent = ArrowDownIcon,
		renderValue,
		renderOption,
		...selectProps
	} = props;

	const selectedOption = useMemo(() => options?.find(option => (value as string) === option.value), [options, value]);

	const emptyLabel = useMemo(() => placeholder, [placeholder]);

	const renderPlaceholder = useCallback(
		(selectedOption?: SelectOption) => () => (
			<Typography
				component='span'
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: '8px',
					fontFamily: 'Montserrat, sans-serif',
					color: disabled ? Colors.grayDark : selectedOption ? Colors.white : inputPlaceholderColor,
					fontWeight: '400',
				}}
			>
				{selectedOption?.icon} {selectedOption ? selectedOption.label : emptyLabel}
			</Typography>
		),
		[disabled, emptyLabel]
	);

	const selectContainerRef = useRef<HTMLDivElement | null>(null);
	const offsetWidth = selectContainerRef.current?.offsetWidth;

	return (
		<CoreFormControl
			id={id}
			label={label}
			labelSx={labelSx}
			labelHint={labelHint}
			labelHintIconSx={labelHintIconSx}
			placementLabelHint={placementLabelHint}
			placementOffset={placementOffset}
			tooltipSx={tooltipSx}
			requiredMark={requiredMark}
			fullWidth={fullWidth}
			focused={focused}
			controlSx={controlSx}
			helperText={helperText}
			error={error}
			disabled={disabled}
		>
			<Select
				{...selectProps}
				id={id}
				value={value}
				disabled={disabled}
				ref={selectContainerRef}
				placeholder={placeholder}
				displayEmpty={displayEmpty ?? true}
				renderValue={renderValue ?? renderPlaceholder(selectedOption)}
				sx={clsx(
					{
						'& .MuiSelect-icon': {
							top: 'calc(50% - 10px)',
							right: '16px',
							width: '20px',
							height: '20px',
							transform: disableIconRotate ? 'none' : undefined,
							color: Colors.white,
						},

						'&.Mui-disabled .MuiSelect-icon': {
							'& .MuiSelect-icon': {
								color: Colors.white_a20,
							},

							'.MuiSelect-select': {
								color: Colors.grayDark,

								'& .MuiSelect-icon': {
									color: Colors.black_a50,
								},
							},
						},
					},
					sx
				)}
				MenuProps={{
					anchorOrigin: {
						vertical: 'bottom',
						horizontal: 'left',
					},
					transformOrigin: {
						vertical: 'top',
						horizontal: 'left',
					},
					...(MenuProps ?? {}),
					PaperProps: {
						...(MenuProps?.PaperProps ?? {}),
						sx: clsx(
							{
								width: fitMenuWidth
									? (menuMinWidth ?? 0) > (offsetWidth ?? 0)
										? menuMinWidth
										: offsetWidth
									: undefined,
							},
							selectPaperStyles,
							MenuProps?.PaperProps?.sx,
						),
					},
				}}
				SelectDisplayProps={{
					...(SelectDisplayProps ?? {}),
					style: {
						paddingRight: 46,
						...(SelectDisplayProps?.style ?? {})
					},
				}}
				IconComponent={IconComponent}
				input={<CoreInput {...inputProps} ref={ref} />}
			>
				{children}
				{options?.map(renderOption ?? (({ label, value, icon }: SelectOption) => (
					<CoreSelectMenuItem
						key={value}
						value={value}
						noWrap
						{...MenuItemProps}
						textSx={clsx(
							{
								display: 'flex',
								alignItems: 'center',
								gap: '8px',
							},
							MenuItemProps?.textSx
						)}
					>
						{icon} {label}
					</CoreSelectMenuItem>
				)))}
			</Select>
		</CoreFormControl>
	);
};

export const CoreSelect = forwardRef(CoreSelectBase) as <T>(
	props: CoreSelectProps<T> & { ref?: Ref<HTMLElement>}
) => ReturnType<typeof CoreSelectBase>;
